import { forwardRef } from 'react';
import classnames from 'classnames/bind';

import styles from './mochi-button.module.scss';
const cx = classnames.bind(styles);

type ButtonVariant =
  | 'solid'
  | 'solid-round'
  | 'outline'
  | 'outline-round'
  | 'text-button'
  | 'text';

export interface MochiButtonProps
  extends Omit<
    React.DetailedHTMLProps<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      HTMLButtonElement
    >,
    'ref'
  > {
  variant?: ButtonVariant;
  theme?:
    | 'primary'
    | 'primary2'
    | 'secondary'
    /** @deprecated */
    | 'black'
    | 'grey'
    | 'blue';
  size?: 'small' | 'normal' | 'large' | 'full';
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  isLoading?: boolean;
  isDarkMode?: boolean;
}

export const MochiButton = forwardRef<HTMLButtonElement, MochiButtonProps>(
  (props, ref) => {
    const {
      variant = 'solid',
      theme = 'primary',
      size = 'normal',
      iconLeft,
      iconRight,
      isLoading = false,
      isDarkMode = false,
      children,
      className,
      ...rest
    } = props;

    const buttonClass = cx({
      button: true,
      [variant]: true,
      [size]: true,
      [theme]: true,
      'light-mode': !isDarkMode,
      'dark-mode': isDarkMode,
      'icon-left': !!iconLeft,
      'icon-right': !!iconRight,
      'with-icon': !!iconLeft || !!iconRight,
      loading: isLoading,
      disabled: rest.disabled,
    });

    return (
      <button className={`${buttonClass} ${className}`} ref={ref} {...rest}>
        {iconLeft}
        {isLoading ? (
          <LoadingEllipse
            isDarkMode={isDarkMode}
            variant={variant}
            theme={theme}
          />
        ) : (
          children
        )}
        {iconRight}
      </button>
    );
  },
);

const LoadingEllipse = ({
  variant,
  theme,
  isDarkMode,
}: {
  variant: ButtonVariant;
  theme: string;
  isDarkMode: boolean;
}) => {
  const loadingEllipseClass = cx({
    'loading-ellipse': true,
    [theme]: true,
    [variant]: true,
    'light-mode': !isDarkMode,
    'dark-mode': isDarkMode,
  });
  return (
    <div className={loadingEllipseClass}>
      <div />
      <div />
      <div />
      <div />
    </div>
  );
};
